import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import {
  PERFIS, PERFIL_CONFIG, KYC_REQUISITOS, KYC_STATUS_PADRAO, MENU_CONSOLIDADO,
} from "../constants/perfis";

// PerfilContext mantém qual dos 3 perfis o usuário está usando agora
// (locatário/locador/freteiro), o status de KYC localStorage-cached, e
// expõe `verificarKYC(perfil)` que cruza o cache local com as flags reais
// vindas do backend (kyc_cpf_aprovado, face_verificado, etc) — admins
// passam direto.
const PerfilContext = createContext(null);

export const usePerfil = () => useContext(PerfilContext);

export const PerfilProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id || null;

  const _perfilKey = (uid) => uid ? `toalugando_perfil_${uid}` : null;
  const _kycKey   = (uid) => uid ? `toalugando_kyc_${uid}` : null;

  const [perfilAtivo, setPerfilAtivo] = useState(() => {
    try {
      const savedUser = localStorage.getItem('toalugando_user');
      const u = savedUser ? JSON.parse(savedUser) : null;
      const uid = u?.id;
      const saved = uid && localStorage.getItem(`toalugando_perfil_${uid}`);
      if (saved) return saved;
      // Sem cache local — deriva do user salvo (caso: hard refresh ou first paint)
      if (u?.locador_habilitado) return PERFIS.LOCADOR;
      if (u?.freteiro_habilitado) return PERFIS.FRETEIRO;
      return PERFIS.LOCATARIO;
    } catch (e) {
      return PERFIS.LOCATARIO;
    }
  });

  const [kycStatus, setKycStatus] = useState(() => {
    try {
      const savedUser = localStorage.getItem('toalugando_user');
      const uid = savedUser ? JSON.parse(savedUser)?.id : null;
      if (uid) {
        const saved = localStorage.getItem(`toalugando_kyc_${uid}`);
        if (saved) return JSON.parse(saved);
      }
    } catch (e) {}
    return KYC_STATUS_PADRAO;
  });

  // Resetar estado quando o usuário muda (login/logout)
  // Auto-detecta o perfil correto baseado nas flags do backend quando localStorage
  // estiver vazio (caso típico: login em novo dispositivo). Sem isso o user
  // cai em "Particular" e nao ve equipamentos/pedidos recebidos do perfil Locador.
  useEffect(() => {
    if (!userId) {
      setPerfilAtivo(PERFIS.LOCATARIO);
      setKycStatus(KYC_STATUS_PADRAO);
      return;
    }
    try {
      const savedPerfil = localStorage.getItem(_perfilKey(userId));
      if (savedPerfil) {
        setPerfilAtivo(savedPerfil);
      } else {
        // Sem perfil salvo localmente — deriva do backend.
        // Prioridade: Locador > Freteiro > Locatário (perfil "mais ativo").
        let perfilDerivado = PERFIS.LOCATARIO;
        if (user?.locador_habilitado) perfilDerivado = PERFIS.LOCADOR;
        else if (user?.freteiro_habilitado) perfilDerivado = PERFIS.FRETEIRO;
        setPerfilAtivo(perfilDerivado);
        // Persiste pra próximos uses no MESMO device
        try { localStorage.setItem(_perfilKey(userId), perfilDerivado); } catch {}
      }
      const savedKyc = localStorage.getItem(_kycKey(userId));
      setKycStatus(savedKyc ? JSON.parse(savedKyc) : KYC_STATUS_PADRAO);
    } catch (e) {
      setPerfilAtivo(PERFIS.LOCATARIO);
      setKycStatus(KYC_STATUS_PADRAO);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user?.locador_habilitado, user?.freteiro_habilitado]);

  const alterarPerfil = (novoPerfil) => {
    setPerfilAtivo(novoPerfil);
    try {
      if (userId) localStorage.setItem(_perfilKey(userId), novoPerfil);
    } catch (e) {}
  };

  const verificarKYC = (perfil) => {
    const status = kycStatus[perfil] || { aprovado: false, pendente: false, documentos: {} };
    const requisitos = KYC_REQUISITOS[perfil];
    const docsObrigatorios = requisitos?.documentos.filter((d) => d.obrigatorio) || [];

    // Status real vindo do backend (user.kyc_locatario / kyc_locador / kyc_freteiro)
    const kycBackend = user?.[`kyc_${perfil}`];
    const aprovadoBackend = kycBackend === 'aprovado';
    const pendenteBackend = kycBackend === 'em_analise' || kycBackend === 'pendente';

    // Validação cruzada com flags individuais do KYC base — garante que todos
    // os 4 itens (CPF, antecedentes, face, endereço) realmente foram aprovados.
    // Admins não passam por essa checagem (têm aprovação manual).
    const flagsBaseCompletas = !!(
      user?.kyc_cpf_aprovado &&
      user?.kyc_antecedentes_aprovado &&
      user?.face_verificado &&
      user?.kyc_endereco_aprovado
    );

    // O KYC BASE É UM SÓ: as 4 validações (CPF na Receita, antecedentes,
    // biometria facial e comprovante de endereço) são AS MESMAS pra Particular
    // e pra Locador pessoa física — quem passou por elas está verificado nos
    // dois papéis. O backend já opera assim (anunciar, sacar e o selo
    // "Verificado" aceitam kyc_locatario='aprovado'), mas esta tela lia SÓ
    // kyc_locador, que só vira 'aprovado' na Locadora PJ (revisão do admin +
    // Contrato Master). Resultado: quem estava 4 de 4 via "Locador não
    // verificado · 0%" (relato do dono com print, 06/08).
    // FRETEIRO fica de fora de propósito: exige CNH, CRLV e fotos do veículo
    // ALÉM da base — lá a exigência extra é real.
    const compartilhaKycBase = perfil === PERFIS.LOCATARIO || perfil === PERFIS.LOCADOR;
    const aprovadoBase = aprovadoBackend
      || (compartilhaKycBase && user?.kyc_locatario === 'aprovado');
    const aprovadoReal = aprovadoBase && (user?.is_admin || flagsBaseCompletas);

    const docsAprovados = Object.keys(status.documentos || {}).filter((k) => status.documentos[k]);
    let progresso;
    if (aprovadoReal) {
      progresso = 100;
    } else {
      // Vale o MAIOR entre o cache local de documentos e as etapas do KYC base
      // já aprovadas no backend. Antes o ramo das etapas só rodava quando o
      // backend dizia 'aprovado', então um locador com as 4 etapas OK e
      // kyc_locador='pendente' caía no cache vazio e mostrava 0%.
      const porDocs = docsObrigatorios.length > 0
        ? Math.round((docsAprovados.length / docsObrigatorios.length) * 100)
        : 0;
      const etapasConcluidas = [
        user?.kyc_cpf_aprovado,
        user?.kyc_antecedentes_aprovado,
        user?.face_verificado,
        user?.kyc_endereco_aprovado,
      ].filter(Boolean).length;
      // Etapas base contam pro Particular/Locador sempre; pro Freteiro só no
      // caso antigo (backend aprovado com etapa base faltando), sem inflar a
      // barra dele — lá a base é subconjunto de uma lista maior.
      const porBase = (compartilhaKycBase || aprovadoBackend)
        ? Math.round((etapasConcluidas / 4) * 100)
        : 0;
      progresso = Math.max(porDocs, porBase);
    }

    return {
      ...status,
      aprovado: status.aprovado || aprovadoReal,
      // "pendente" nunca convive com "liberado": o locador PF fica com
      // kyc_locador='pendente' pra sempre (esse campo é da Locadora PJ), e sem
      // esse guarda ele apareceria liberado E pendente ao mesmo tempo.
      pendente: !aprovadoReal && (
        status.pendente || pendenteBackend || (aprovadoBase && !flagsBaseCompletas)
      ),
      requisitos,
      progresso,
      liberado: status.aprovado || aprovadoReal,
      flags_base: {
        cpf: !!user?.kyc_cpf_aprovado,
        antecedentes: !!user?.kyc_antecedentes_aprovado,
        face: !!user?.face_verificado,
        endereco: !!user?.kyc_endereco_aprovado,
      },
    };
  };

  const atualizarKYC = (perfil, updates) => {
    const novoStatus = {
      ...kycStatus,
      [perfil]: { ...kycStatus[perfil], ...updates },
    };
    setKycStatus(novoStatus);
    try {
      if (userId) localStorage.setItem(_kycKey(userId), JSON.stringify(novoStatus));
    } catch (e) {}
  };

  const config = PERFIL_CONFIG[perfilAtivo];

  return (
    <PerfilContext.Provider
      value={{
        perfilAtivo, alterarPerfil, config,
        verificarKYC, atualizarKYC,
        kycStatus, setKycStatus,
        MENU_CONSOLIDADO, KYC_REQUISITOS,
      }}
    >
      {children}
    </PerfilContext.Provider>
  );
};
